import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  ChevronRight,
  Check,
  Play,
  X,
  ShieldCheck,
  Target,
  Eye,
  Brain,
  ArrowUpRight,
  Plus,
  Minus,
} from "lucide-react";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { philosophyText, objectives, ASSETS } from "../mock";

gsap.registerPlugin(ScrollTrigger);

const mandateBg =
  "https://images.unsplash.com/photo-1497633762265-9d179a990aa6?auto=format&fit=crop&w=1800&q=80";

const childBg = "/assets/images/child.png";

const tabs = [
  {
    id: "mission",
    title: "Our Mission",
    icon: Target,
    body:
      "To provide inclusive, technology-driven, and globally competitive basic education that equips every child in Akwa Ibom State with the knowledge, values, and skills needed for personal growth, responsible citizenship, and future leadership.",
  },
  {
    id: "vision",
    title: "Our Vision",
    icon: Eye,
    body:
      "To build a basic education system where every Akwa Ibom child can dream, learn, grow, and lead with confidence.",
  },
  {
    id: "commitment",
    title: "Our Commitment",
    icon: Brain,
    body:
      "We are committed to improving learning environments, supporting teachers, strengthening school supervision, promoting digital education, and ensuring that public schools remain safe, inclusive, child-friendly, and capable of delivering quality education for every learner.",
  },
];

const mandates = [
  {
    id: 1,
    title: "Access to Quality Basic Education",
    description:
      "AKSUBEB promotes free, compulsory, inclusive, and quality basic education for children in public primary and junior secondary schools across Akwa Ibom State.",
  },
  {
    id: 2,
    title: "Improvement of Public Schools",
    description:
      "The Board supports the improvement of public primary and junior secondary schools through planning, monitoring, renovation, and institutional support.",
  },
  {
    id: 3,
    title: "Teacher Recruitment, Training and Support",
    description:
      "AKSUBEB strengthens teaching quality through teacher recruitment, training, professional development, monitoring, and classroom support systems.",
  },
  {
    id: 4,
    title: "Learning Materials and Infrastructure",
    description:
      "The Board provides learning materials, furniture, classrooms, offices, libraries, and other facilities needed to improve teaching and learning outcomes.",
  },
  {
    id: 5,
    title: "Inclusive Education for Every Child",
    description:
      "AKSUBEB promotes equal learning opportunities for children regardless of location, background, economic status, or physical circumstance.",
  },
  {
    id: 6,
    title: "Community Collaboration and School Protection",
    description:
      "The Board works with communities, school leaders, and local stakeholders to protect, sustain, and improve public school facilities.",
  },
];

const AboutPage = () => {
  const [active, setActive] = useState("mission");
  const [videoOpen, setVideoOpen] = useState(false);
  const [openMandate, setOpenMandate] = useState(1);

  const current = tabs.find((t) => t.id === active);
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", {
        opacity: 0,
        y: 30,
        duration: 0.7,
        ease: "power3.out",
      });

      gsap.from(".hero-title", {
        opacity: 0,
        y: 80,
        duration: 1.1,
        ease: "power4.out",
      });

      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        delay: 0.2,
        ease: "power3.out",
      });

      gsap.utils.toArray(".reveal-up").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 70,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
          },
        });
      });

      gsap.utils.toArray(".card-animate").forEach((el) => {
        gsap.from(el, {
          opacity: 0,
          y: 45,
          duration: 0.7,
          ease: "power3.out",
          scrollTrigger: {
            trigger: el,
            start: "top 90%",
          },
        });
      });

      gsap.to(".floating-orb-1", {
        y: 40,
        x: -20,
        duration: 6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".floating-orb-2", {
        y: -40,
        x: 20,
        duration: 7,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".cta-float-one", {
        y: 35,
        x: -20,
        duration: 5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".cta-float-two", {
        y: -30,
        x: 20,
        duration: 6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <main ref={pageRef} className="bg-white overflow-hidden">
      {/* HERO */}
      <section className="relative min-h-screen text-white flex items-center overflow-hidden">
        <img
          src={ASSETS.classroom}
          alt="AKSUBEB Background"
          className="absolute inset-0 w-full h-full object-cover"
        />

        <div className="absolute inset-0 bg-[#04160a]/80" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#0b6b1d]/70 to-[#ff6600]/30" />

        <div className="floating-orb-1 absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-[#0b6b1d]/40 blur-3xl" />
        <div className="floating-orb-2 absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#ff6600]/25 blur-3xl" />

        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:80px_80px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-32">
          <div className="hero-badge inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 backdrop-blur-md mb-8">
            <span className="w-2 h-2 rounded-full bg-[#ff6600]" />
            Institutional Profile
          </div>

          <h1
            className="hero-title text-6xl md:text-8xl font-black leading-[0.95] max-w-5xl"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            About AKSUBEB
          </h1>

          <p className="hero-text mt-8 max-w-2xl text-white/85 text-lg leading-[1.9]">
            Akwa Ibom State Universal Basic Education Board is committed to
            strengthening basic education for every child across Akwa Ibom State.
          </p>

          <div className="hero-text flex items-center gap-2 mt-8 text-sm text-white/75">
            <Link
              to="/"
              className="flex items-center gap-1 hover:text-[#ff6600] transition-colors"
            >
              <Home size={14} />
              Home
            </Link>

            <ChevronRight size={14} />

            <span className="text-[#ff6600]">About</span>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
          <div className="relative reveal-up">
            <div className="absolute -top-10 -left-10 w-52 h-52 rounded-full bg-[#0b6b1d]/10 blur-3xl" />

            <div className="relative rounded-[40px] overflow-hidden shadow-[0_40px_120px_-20px_rgba(11,107,29,0.35)]">
              <img
                src={ASSETS.classroom}
                alt="AKSUBEB classroom"
                className="w-full h-[650px] object-cover hover:scale-105 transition-transform duration-700"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-[#04160a]/80 via-transparent to-transparent" />

              <div className="absolute bottom-8 left-8 right-8 bg-white rounded-3xl p-7 shadow-2xl">
                <p className="text-[#ff6600] font-black tracking-[0.2em] uppercase text-xs mb-2">
                  About Us
                </p>

                <h3
                  className="text-[#0b6b1d] text-3xl font-black leading-tight"
                  style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                >
                  Akwa Ibom State Universal Basic Education Board
                </h3>
              </div>
            </div>
          </div>

          <div className="reveal-up">
            <p className="text-[#0b6b1d] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Who We Are
            </p>

            <h2
              className="text-5xl md:text-6xl font-black leading-tight text-[#0b6b1d] mb-8"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Strengthening Basic Education Across{" "}
              <span className="text-[#ff6600]">Akwa Ibom State</span>
            </h2>

            <div className="space-y-6 text-slate-700 leading-[2] text-[16.5px]">
              <p>
                The Akwa Ibom State Universal Basic Education Board, AKSUBEB,
                is committed to strengthening basic education across Akwa Ibom
                State.
              </p>

              <p>
                The Board works to ensure that every child of school-going age
                has access to quality, inclusive, and functional basic education.
              </p>

              <p>
                Guided by the ARISE Agenda of Governor Umo Eno, AKSUBEB
                supports the delivery of strong foundational learning for
                children in public primary and junior secondary schools.
              </p>

              <p>
                The Board focuses on access, equity, teacher quality, school
                infrastructure, curriculum improvement, digital learning, and
                community participation in education.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MANDATE */}
      <section
        className="relative py-32 overflow-hidden bg-[#04160a]"
        style={{
          backgroundImage: `url(${mandateBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 bg-[#04160a]/55" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/70 via-[#0b6b1d]/45 to-[#ff6600]/18" />

        <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-[#0b6b1d]/25 blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#ff6600]/20 blur-3xl animate-pulse" />

        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 items-start">
          <div className="reveal-up">
            <p className="text-[#ff6600] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Our Mandate
            </p>

            <h2
              className="text-5xl md:text-6xl font-black text-white leading-tight mb-6"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Supporting Universal Basic Education Implementation
            </h2>

            <p className="text-white/85 leading-[1.9] text-[17px] max-w-2xl">
              AKSUBEB works to improve access, quality, learning environments,
              teacher capacity, infrastructure, inclusion, and community
              participation across public basic schools in Akwa Ibom State.
            </p>
          </div>

          <div className="space-y-4 reveal-up">
            {mandates.map((item) => {
              const open = openMandate === item.id;

              return (
                <div
                  key={item.id}
                  className="bg-white/95 backdrop-blur-md rounded-[24px] overflow-hidden shadow-xl border border-white/30"
                >
                  <button
                    onClick={() => setOpenMandate(open ? null : item.id)}
                    className={`w-full flex items-center justify-between gap-5 px-6 py-5 text-left transition-all duration-300 ${
                      open
                        ? "bg-[#0b6b1d] text-white"
                        : "bg-white/95 text-[#0b6b1d] hover:bg-[#0b6b1d] hover:text-white"
                    }`}
                  >
                    <span className="flex items-center gap-4">
                      <span
                        className={`w-11 h-11 rounded-2xl flex items-center justify-center font-black transition-all duration-300 ${
                          open
                            ? "bg-[#ff6600] text-white"
                            : "bg-[#0b6b1d]/10 text-[#0b6b1d]"
                        }`}
                      >
                        {String(item.id).padStart(2, "0")}
                      </span>

                      <span className="font-black text-[17px] leading-snug">
                        {item.title}
                      </span>
                    </span>

                    <span className="flex-shrink-0 text-[#ff6600]">
                      {open ? <Minus size={22} /> : <Plus size={22} />}
                    </span>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      open ? "max-h-56" : "max-h-0"
                    }`}
                  >
                    <p className="px-6 py-5 text-slate-700 leading-[1.85] text-[15.5px] border-t border-slate-100">
                      {item.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">
          <div className="reveal-up">
            <p className="text-[#0b6b1d] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Vision, Mission & Commitment
            </p>

            <h2
              className="text-5xl md:text-6xl font-black text-[#0b6b1d] leading-tight mb-10"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              A Future-Ready Education System
            </h2>

            <div className="flex flex-wrap gap-4 mb-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;

                return (
                  <button
                    key={tab.id}
                    onClick={() => setActive(tab.id)}
                    className={`px-6 py-4 rounded-2xl flex items-center gap-3 transition-all duration-300 ${
                      active === tab.id
                        ? "bg-[#0b6b1d] text-white shadow-xl"
                        : "bg-slate-100 text-[#0b6b1d] hover:bg-slate-200"
                    }`}
                  >
                    <Icon size={20} />
                    <span className="font-black">{tab.title}</span>
                  </button>
                );
              })}
            </div>

            <div className="bg-white border border-slate-100 rounded-[35px] p-10 shadow-[0_40px_100px_-20px_rgba(11,107,29,0.18)]">
              <h3
                className="text-3xl font-black text-[#0b6b1d] mb-5"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                {current.title}
              </h3>

              <p className="text-slate-700 leading-[2] text-[16px]">
                {current.body}
              </p>
            </div>
          </div>

          <div className="relative reveal-up">
            <div className="absolute -top-8 -right-8 w-full h-full rounded-[40px] bg-[#0b6b1d]/10" />

            <div className="relative rounded-[40px] overflow-hidden shadow-[0_45px_120px_-20px_rgba(11,107,29,0.35)]">
              <img
                src={ASSETS.educator}
                alt="AKSUBEB educator"
                className="w-full h-[700px] object-cover hover:scale-105 transition-transform duration-700"
              />

              <div className="absolute inset-0 bg-gradient-to-t from-[#04160a]/80 via-transparent to-transparent" />

              <button
                onClick={() => setVideoOpen(true)}
                className="absolute inset-0 flex items-center justify-center"
                aria-label="Play introduction video"
              >
                <div className="w-24 h-24 rounded-full bg-white text-[#ff6600] flex items-center justify-center shadow-2xl hover:scale-110 transition-transform">
                  <Play size={34} fill="#ff6600" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="relative py-32 bg-[#04160a] text-white overflow-hidden">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full bg-[#0b6b1d]/30 blur-3xl" />

        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full bg-[#ff6600]/20 blur-3xl" />

        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 relative z-10">
          <div className="reveal-up">
            <p className="text-[#ff6600] font-black tracking-[0.2em] uppercase text-xs mb-4">
              Educational Philosophy
            </p>

            <h2
              className="text-5xl md:text-6xl font-black leading-tight mb-8"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Building The Future Through Quality Education
            </h2>

            <div className="space-y-6 text-white/80 leading-[2]">
              {philosophyText.map((text) => (
                <p key={text.slice(0, 40)}>{text}</p>
              ))}
            </div>
          </div>

          <div className="space-y-5">
            {objectives.map((obj) => (
              <div
                key={obj}
                className="card-animate group bg-white/10 border border-white/10 backdrop-blur-md rounded-[28px] p-6 hover:bg-white transition-all duration-500"
              >
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-[#ff6600] text-white flex items-center justify-center flex-shrink-0">
                    <Check size={20} />
                  </div>

                  <p className="text-white/85 group-hover:text-slate-700 leading-relaxed">
                    {obj}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="relative py-36 overflow-hidden bg-[#04160a]"
        style={{
          backgroundImage: `url(${childBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        <div className="absolute inset-0 bg-[#04160a]/58" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/80 via-[#0b6b1d]/55 to-[#ff6600]/20" />

        <div className="cta-float-one absolute bottom-[-180px] right-[-160px] w-[560px] h-[560px] rounded-full bg-[#0b6b1d]/35 blur-3xl" />
        <div className="cta-float-two absolute bottom-[-120px] right-[80px] w-[380px] h-[380px] rounded-full bg-[#ff6600]/35 blur-3xl" />

        <div className="absolute bottom-0 right-0 w-[460px] h-[460px] bg-gradient-to-tl from-[#ff6600]/25 via-[#0b6b1d]/20 to-transparent rounded-tl-full" />

        <div className="relative max-w-5xl mx-auto px-6 text-center reveal-up">
          <div className="w-24 h-24 rounded-[30px] bg-white/15 backdrop-blur-md text-white flex items-center justify-center mx-auto mb-8 shadow-[0_20px_50px_-20px_rgba(255,255,255,0.45)] border border-white/20">
            <ShieldCheck size={44} />
          </div>

          <p className="text-[#ff6600] font-black tracking-[0.25em] uppercase text-xs mb-4">
            Our Promise
          </p>

          <h2
            className="text-5xl md:text-6xl font-black text-white leading-tight mb-6 drop-shadow-lg"
            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
          >
            Every Child Deserves A Strong Foundation
          </h2>

          <p className="text-white/85 leading-[2] text-lg max-w-3xl mx-auto">
            AKSUBEB remains committed to building a stronger, safer, more
            inclusive, and future-ready education system.
          </p>

          <Link
            to="/programs"
            className="group inline-flex items-center gap-3 mt-10 px-8 py-4 rounded-full bg-[#ff6600] hover:bg-[#0b6b1d] text-white font-black transition-all duration-500 shadow-xl hover:scale-105"
          >
            Explore Our Programs
            <ArrowUpRight
              size={20}
              className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300"
            />
          </Link>
        </div>
      </section>

      {/* VIDEO MODAL */}
      {videoOpen && (
        <div
          onClick={() => setVideoOpen(false)}
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-5xl aspect-video"
          >
            <button
              onClick={() => setVideoOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-[#ff6600]"
              aria-label="Close video"
            >
              <X size={30} />
            </button>

            <iframe
              className="w-full h-full rounded-2xl"
              src="https://www.youtube.com/embed/JqEjEh8X4Pg?autoplay=1"
              title="AKSUBEB"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </main>
  );
};

export default AboutPage;